import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { CircularProgress } from 'react-md/lib/Progress'
import ViiluDialog from 'ui/components/ViiluDialog'
import { createEventInputs } from './constants'
import { Controller, useForm, useWatch } from 'react-hook-form'
import TextField from 'react-md/lib/TextFields'
import type { LoanEventSorting } from '../types'
import SelectField from 'react-md/lib/SelectFields'
import { CreateFormGrid, CreateFormGridItem } from '../../components'
import styled from 'styled-components'
import DatePickerISO from 'ui/components/DatePickerISO'
import { _createLoanEvent } from 'state/finance-loan.actions'
import { convertEmptyStringsToNull } from '../utils'
import { useDispatch } from 'react-redux'
import * as notifyActions from 'state/notifyActions'
import { useCompanyApartments } from 'util/hooks/useCompanyApartments'
import type { SelectOptions } from 'ui/ManagerFinance/types'
import { LoanEventType } from '@viilu-web/shared/enums/loans'
import { useZodValidationResolver } from 'util/hooks/useZodValidationResolver'
import {
	createEventFormSchemaBasedOnEventType,
	requiredCreateEventFields,
} from './validation'
import type { LoanEventCreateFormInputs } from './validation'
import LoanEventCreateDialogSharegroupTable from './LoanEventCreateDialogSharegroupTable'
import { createApartmentOptions } from '@viilu-web/shared/util/apartmentOptions'
import naturalSort from 'javascript-natural-sort'
import { useBuildings } from 'ui/CompanyBuildings/hooks'
import { NumericTextField } from 'ui/components/NumericTextField'
import LoanEventCreateAdjustedAmountInput from './LoanEventCreateAdjustedAmountInput'
import { ErrorMessage } from 'ui/ManagerFinance/utils/components'
import type {
	CreateLoanEvent,
	Loan,
	LoanCalculation,
	LoanEvent,
	LoanShareholder,
} from '@viilu-web/shared/types/loans'
import LoanEventCreateSharegroupTableAmountInput from './LoanEventCreateSharegroupTableAmountInput'
import LoanEventCreateLoanDrawInput from './LoanEventCreateLoanDrawInput'
import useLoanShareCalculation from './useLoanShareCalculation'
import useLoanCalculationSnapshot from './useLoanCalculationSnapshot'

const FormSubHeading = styled.div`
	margin-top: 1rem;
	margin-bottom: 1rem;
	grid-column: span 2;
`

type Props = {
	loan: Loan
	shareholders: LoanShareholder[]
	companyUUID: string
	event?: LoanEvent
	allEvents: LoanEvent[]
	allEventsInRange: LoanEvent[]
	onHide: () => void
	refreshEvents: () => void
	refreshLoan: () => void
}

function LoanEventCreateDialog({
	loan,
	shareholders,
	companyUUID,
	event,
	allEvents,
	allEventsInRange,
	onHide,
	refreshEvents,
	refreshLoan,
}: Props) {
	const intl = useIntl()
	const dispatch = useDispatch()
	const [processing, setProcessing] = useState(false)
	const [eventType, setEventType] = useState(LoanEventType.LOAN_DRAW)
	const [loanCalculation, setLoanCalculation] = useState<
		LoanCalculation | undefined
	>(undefined)
	const { companyApartments } = useCompanyApartments(companyUUID)
	const { buildings } = useBuildings(companyUUID)

	const schema = event
		? undefined
		: createEventFormSchemaBasedOnEventType(
				intl,
				eventType,
				allEventsInRange,
				loanCalculation,
		  )
	const resolver = useZodValidationResolver(schema)
	const {
		control,
		handleSubmit,
		setError,
		setValue,
		watch,
		trigger,
		formState: { errors },
	} = useForm<LoanEventCreateFormInputs>({
		resolver,
		defaultValues: event
			? {
					...event,
					amountWithExpenses:
						event.amount +
						(event.bankExpenseAmount || 0) +
						(event.otherExpenseAmount || 0),
					interestAmount: event.interestAmount || 0,
					bankExpenseAmount: event.bankExpenseAmount || 0,
					otherExpenseAmount: event.otherExpenseAmount || 0,
					explanation: event.explanation || '',
			  }
			: {
					eventDate: '',
					registerDate: '',
					shareGroupUUID: '',
					type: eventType,
					amount: 0,
					amountWithExpenses: 0,
					units: 0,
					balanceAdjustmentAmount: 0,
					adjustedAmount: 0,
					explanation: '',
					interestAmount: 0,
					interestStartDate: '',
					interestEndDate: '',
					bankExpenseAmount: 0,
					otherExpenseAmount: 0,
					currency: 'EUR',
					sharegroupDebtRepayments: [],
			  },
	})

	const [
		type,
		shareGroupUUID,
		selectedSharegroupDebtRepayments,
		eventDate,
		amount,
		units,
	] = useWatch({
		name: [
			'type',
			'shareGroupUUID',
			'sharegroupDebtRepayments',
			'eventDate',
			'amount',
			'units',
		],
		control,
	})

	const [calculationFilters, setCalculationFilters] = useState({
		startDate: undefined,
		endDate: eventDate,
		companyUUID: companyUUID,
	})

	const { calculation, loading: calculationLoading } = useLoanShareCalculation(
		loan.uuid,
		calculationFilters,
		!!event,
	)
	const { calculationSnapshot, loading: calculationSnapshotLoading } =
		useLoanCalculationSnapshot(
			loan.uuid,
			event?.loanCalculationSnapshotUUID || '',
			!event,
		)
	console.log('useLoanCalculationSnapshot', calculationSnapshot)

	useEffect(() => {
		setCalculationFilters({
			startDate: undefined,
			endDate: eventDate,
			companyUUID,
		})
	}, [eventDate, setCalculationFilters, companyUUID])

	useEffect(() => {
		if (!!event) {
			if (calculationSnapshot?.calculation && !calculationSnapshotLoading) {
				setLoanCalculation(calculationSnapshot.calculation)
			}
		} else {
			if (calculation && !calculationLoading) {
				setLoanCalculation(calculation)
			}
		}
	}, [
		event,
		calculation,
		calculationLoading,
		calculationSnapshot,
		calculationSnapshotLoading,
		setLoanCalculation,
	])

	const apartmentsInLoan = useMemo(() => {
		const selectableApartments: SelectOptions[] = []
		shareholders.forEach((shareholder) => {
			const apartmentMatch = companyApartments.find(
				(apt) => apt.uuid === shareholder.shareGroupUUID,
			)
			if (apartmentMatch) {
				selectableApartments.push({
					value: apartmentMatch?.uuid,
					label: apartmentMatch.identifier,
				})
			}
		})
		selectableApartments.sort((a, b) => {
			return naturalSort(a.label, b.label)
		})
		selectableApartments.unshift({
			value: '',
			label: intl.formatMessage({
				defaultMessage: 'Ei valintaa',
				description: 'No select choice',
			}),
		})
		return selectableApartments
	}, [companyApartments, shareholders, intl])

	const apartmentOptionsWithShareAmount = useMemo(() => {
		const apartmentOptions = createApartmentOptions(
			companyApartments,
			buildings,
		)
		return apartmentOptions.map((aptOption) => {
			return {
				...aptOption,
				loanShareAmount:
					shareholders.find((sh) => sh.shareGroupUUID === aptOption.value)
						?.originalLoanShareAmount ?? 0,
			}
		})
	}, [buildings, companyApartments, shareholders])

	const unusedSharegroupDebtRepaymentEvents = useMemo(() => {
		return allEventsInRange.filter(
			(e) =>
				e.type === LoanEventType.SHAREGROUP_DEBT_REPAYMENT && e.used === false,
		)
	}, [allEventsInRange])

	useEffect(() => {
		setEventType(type)
	}, [type])

	useEffect(() => {
		// Whenever eventDate is set, set the event date also as register date as
		// this is the most common scenario for these dates
		if (eventDate !== '') {
			setValue('registerDate', eventDate)
		}
	}, [eventDate, setValue])

	useEffect(() => {
		if (type === LoanEventType.COLLECTED_SHAREGROUP_BANK_REPAYMENT) {
			const hasSelection =
				selectedSharegroupDebtRepayments &&
				selectedSharegroupDebtRepayments.length > 0
			const { repaidUnits, totalRepaymentAmount } = hasSelection
				? selectedSharegroupDebtRepayments.reduce(
						(acc, curr) => {
							const matchingEvent = unusedSharegroupDebtRepaymentEvents.find(
								(e) => e.uuid === curr,
							)
							acc.repaidUnits += matchingEvent?.units || 0
							acc.totalRepaymentAmount +=
								Number(matchingEvent?.adjustedAmount) ?? 0
							return acc
						},
						{ repaidUnits: 0, totalRepaymentAmount: 0 },
				  )
				: { repaidUnits: 0, totalRepaymentAmount: 0 }

			if (totalRepaymentAmount !== amount) {
				setValue('amount', totalRepaymentAmount)
			}
			if (repaidUnits !== units) {
				setValue('units', repaidUnits)
			}
			trigger('sharegroupDebtRepayments')
		}
	}, [
		unusedSharegroupDebtRepaymentEvents,
		selectedSharegroupDebtRepayments,
		setValue,
		trigger,
		amount,
		units,
		type,
	])

	const [sort, setSort] = useState<LoanEventSorting>({
		column: 'eventDate',
		order: 'desc',
	})

	const changeSort = (key: keyof LoanEvent) => {
		if (sort.column === key) {
			setSort((prevData) => ({
				...prevData,
				order: sort.order === 'asc' ? 'desc' : 'asc',
			}))
		} else {
			setSort({
				column: key,
				order: 'asc',
			})
		}
	}

	const drawEventLimitReached = useMemo(() => {
		if (type === LoanEventType.LOAN_DRAW) {
			const allLoanDrawEvents = allEvents.filter(
				(e) => e.type === LoanEventType.LOAN_DRAW,
			)
			// We are only interested if created draw events have caused the limit to be reached
			// If the loan was fully drawn without events, we can check that fron the loan object
			if (allLoanDrawEvents.length === 0) return false

			const originalDrawableAmount =
				(loan?.originalLoanAmount || 0) - (loan?.amountDrawn || 0)
			return (
				allLoanDrawEvents.reduce(
					(acc, curr) => acc + (Number(curr.amount || 0) || 0),
					0,
				) >= originalDrawableAmount
			)
		}
		return false
	}, [type, loan, allEvents])

	const submitIsDisabled = useMemo(() => {
		if (type === LoanEventType.COLLECTED_SHAREGROUP_BANK_REPAYMENT) {
			return selectedSharegroupDebtRepayments?.length === 0
		} else if (type === LoanEventType.LOAN_DRAW) {
			if (drawEventLimitReached) return true
			if (
				loan.amountDrawn &&
				loan.originalLoanAmount &&
				loan.originalLoanAmount <= loan.amountDrawn
			)
				return true
			const drawableAmount =
				(loanCalculation?.originalLoanAmount || 0) -
				(loanCalculation?.loanDrawnAmount || 0)
			return drawableAmount <= 0
		}
		return false
	}, [
		type,
		selectedSharegroupDebtRepayments,
		loanCalculation,
		drawEventLimitReached,
		loan,
	])

	const actions = [
		{
			id: 'dialog-cancel',
			children: intl.formatMessage({
				defaultMessage: 'Peruuta',
				description: 'Cancel button',
			}),
			onClick: onHide,
		},
		...(event === undefined
			? [
					{
						id: 'dialog-ok',
						secondary: true,
						disabled: submitIsDisabled,
						children: processing ? (
							<CircularProgress id="progress" style={{ marginTop: 0 }} />
						) : (
							intl.formatMessage({
								defaultMessage: 'Tallenna',
								description: 'Save button',
							})
						),
						onClick: () => onSubmit(),
					},
			  ]
			: []),
	]

	const onSubmit = handleSubmit(async (data) => {
		// Manual validation for event date if type is interest_change
		if (data.type === LoanEventType.INTEREST_CHANGE) {
			const eventDateExists = allEventsInRange
				.filter((e) => e.type === LoanEventType.INTEREST_CHANGE)
				.some((event) => {
					const eventOnlyDate = event.eventDate.split('T')[0]
					return eventOnlyDate === data.eventDate
				})
			if (eventDateExists) {
				setError('eventDate', {
					message: intl.formatMessage({
						defaultMessage: 'Tapahtumapäivälle on jo kirjattu koron muutos',
						description:
							'A interest change has already been made on the event date',
					}),
				})
				return
			} else {
				setError('eventDate', {})
			}
		}
		setProcessing(true)
		try {
			const sanitizedData = convertEmptyStringsToNull(data)
			const createLoanEventData: CreateLoanEvent = {
				type: sanitizedData.type,
				loanUUID: loan.uuid,
				eventDate: sanitizedData.eventDate,
				registerDate: sanitizedData.registerDate,
				amount:
					eventType === LoanEventType.SHAREGROUP_DEBT_REPAYMENT
						? sanitizedData.amountWithExpenses
						: sanitizedData.amount,
				units: sanitizedData.units,
				balanceAdjustmentAmount: sanitizedData.balanceAdjustmentAmount,
				adjustedAmount: sanitizedData.adjustedAmount,
				shareGroupUUID: sanitizedData.shareGroupUUID,
				eventsUsed: data.sharegroupDebtRepayments,
				interestAmount:
					sanitizedData.interestAmount && sanitizedData.interestAmount > 0
						? sanitizedData.interestAmount
						: undefined,
				interestStartDate: sanitizedData.interestStartDate,
				interestEndDate: sanitizedData.interestEndDate,
				bankExpenseAmount:
					sanitizedData.bankExpenseAmount && sanitizedData.bankExpenseAmount > 0
						? sanitizedData.bankExpenseAmount
						: undefined,
				otherExpenseAmount:
					sanitizedData.otherExpenseAmount &&
					sanitizedData.otherExpenseAmount > 0
						? sanitizedData.otherExpenseAmount
						: undefined,
				explanation: sanitizedData.explanation,
				currency: 'EUR',
			}
			const result = await _createLoanEvent(loan.uuid, createLoanEventData)
			if (result?.ok) {
				dispatch(
					notifyActions.success(
						intl.formatMessage({
							defaultMessage: 'Tapahtuma luotiin onnistuneesti.',
							description:
								'Notification message when loan event has been succesfully created',
						}),
					),
				)
				refreshEvents()
				refreshLoan()
				onHide()
			} else {
				dispatch(
					notifyActions.error(
						intl.formatMessage({
							defaultMessage: 'Tapahtuman luonti ei onnistunut.',
							description:
								'Notification message when there was error during loan event creation',
						}),
					),
				)
				throw new Error('Response is not ok.')
			}
		} catch (error) {
			console.error(error)
			setProcessing(false)
		}
	})

	const renderInputs = () => {
		return createEventInputs.map((input) => {
			const {
				show,
				showOnCondition,
				key,
				title,
				type,
				disabled,
				fullWidth,
				menuItems,
				min,
			} = input

			const isDisabled = event ? true : !!disabled

			let usedTitle = title(intl)
			// In case of dynamic title
			if (key === 'amount') {
				usedTitle = title(intl, eventType)
			}

			let usedMenuItems = menuItems ? menuItems(intl) : undefined
			if (key === 'shareGroupUUID') {
				usedMenuItems = menuItems
					? menuItems(intl, apartmentsInLoan)
					: undefined
			}

			if (key === 'sub-heading') {
				return (
					<FormSubHeading key={`${key}-${usedTitle}`}>
						<h3 className="md-subheading-2" style={{ marginBottom: '0' }}>
							{usedTitle}
						</h3>
						<hr style={{ borderColor: 'rgba(0, 0, 0, 0.2)' }} />
					</FormSubHeading>
				)
			}

			const isRequired = requiredCreateEventFields.includes(key)

			if (show || (showOnCondition && showOnCondition(watch))) {
				if (type === 'text') {
					return (
						<CreateFormGridItem key={key} $fullWidth={fullWidth}>
							<Controller
								name={key}
								control={control}
								render={({ field: { onChange, value } }) => (
									<TextField
										id={key}
										label={usedTitle}
										required={isRequired}
										lineDirection="center"
										floating
										fullWidth
										disabled={isDisabled}
										error={errors[key] !== undefined}
										type="text"
										//@ts-ignore
										autoComplete="never-autocomplete"
										value={value as string}
										onChange={(value) => onChange(value)}
									/>
								)}
							/>
							{errors[key]?.message && (
								<ErrorMessage>{errors[key]?.message}</ErrorMessage>
							)}
						</CreateFormGridItem>
					)
				} else if (type === 'number') {
					if (
						eventType === LoanEventType.SHAREGROUP_DEBT_REPAYMENT &&
						key === 'amount'
					) {
						return (
							<LoanEventCreateAdjustedAmountInput
								key={`${key}-adjusted`}
								calculation={loanCalculation || null}
								shareGroupUUID={shareGroupUUID}
								control={control}
								errors={errors}
								setValue={setValue}
								disabled={isDisabled}
							/>
						)
					} else if (
						eventType === LoanEventType.LOAN_DRAW &&
						key === 'amount'
					) {
						return (
							<LoanEventCreateLoanDrawInput
								key={`${key}-draw`}
								loan={loan}
								shareGroupUUID={shareGroupUUID}
								control={control}
								errors={errors}
								disabled={isDisabled}
								drawEventLimitReached={drawEventLimitReached}
								calculation={loanCalculation}
							/>
						)
					} else if (
						eventType === LoanEventType.COLLECTED_SHAREGROUP_BANK_REPAYMENT &&
						key === 'amount'
					) {
						return (
							<LoanEventCreateSharegroupTableAmountInput
								key={`${key}-collected`}
								control={control}
								errors={errors}
								disabled={isDisabled}
								title={usedTitle}
							/>
						)
					}
					return (
						<CreateFormGridItem key={key} $fullWidth={fullWidth}>
							<Controller
								name={key}
								control={control}
								render={({ field: { onChange, value: currentValue } }) => {
									return (
										<NumericTextField
											id={key}
											label={usedTitle}
											required={isRequired}
											lineDirection="center"
											floating
											fullWidth
											error={errors[key] !== undefined}
											disabled={isDisabled}
											value={currentValue === null ? undefined : currentValue}
											onChange={onChange}
											min={typeof min === 'function' ? min(eventType) : min}
										/>
									)
								}}
							/>
							{errors[key]?.message && (
								<ErrorMessage>{errors[key]?.message}</ErrorMessage>
							)}
						</CreateFormGridItem>
					)
				} else if (type === 'date') {
					return (
						<CreateFormGridItem key={key} $fullWidth={fullWidth}>
							<Controller
								name={key}
								control={control}
								render={({ field: { onChange, value } }) => (
									<DatePickerISO
										style={{
											width: '100%',
										}}
										id={key}
										label={usedTitle}
										error={errors[key] !== undefined}
										value={value as string | Date}
										required={isRequired}
										disabled={isDisabled}
										onChange={(date) => onChange(date)}
									/>
								)}
							/>
							{errors[key]?.message && (
								<ErrorMessage>{errors[key]?.message}</ErrorMessage>
							)}
						</CreateFormGridItem>
					)
				} else if (type === 'select') {
					return (
						<CreateFormGridItem key={key} $fullWidth={fullWidth}>
							<Controller
								name={key}
								control={control}
								render={({ field: { onChange, value } }) => (
									<SelectField
										id={key}
										label={usedTitle}
										required={isRequired}
										style={{
											width: '100%',
										}}
										disabled={isDisabled}
										error={errors[key] !== undefined}
										menuItems={usedMenuItems ? usedMenuItems : []}
										value={value?.toString()}
										onChange={onChange}
									/>
								)}
							/>

							{errors[key]?.message && (
								<ErrorMessage>{errors[key]?.message}</ErrorMessage>
							)}
						</CreateFormGridItem>
					)
				} else if (type === 'textArea') {
					return (
						<CreateFormGridItem key={key} $fullWidth={fullWidth}>
							<Controller
								name={key}
								control={control}
								render={({ field: { onChange, value } }) => (
									<TextField
										id={key}
										label={usedTitle}
										required={isRequired}
										lineDirection="center"
										floating
										fullWidth
										rows={2}
										disabled={isDisabled}
										error={errors[key] !== undefined}
										type="text"
										//@ts-ignore
										autoComplete="never-autocomplete"
										value={value as string}
										onChange={(value) => onChange(value)}
									/>
								)}
							/>
							{errors[key]?.message && (
								<ErrorMessage>{errors[key]?.message}</ErrorMessage>
							)}
						</CreateFormGridItem>
					)
				} else if (type === 'event_table') {
					let events = unusedSharegroupDebtRepaymentEvents
					if (isDisabled && event?.eventsUsed) {
						const usedEvents: LoanEvent[] = []
						event.eventsUsed.forEach((uuid) => {
							const correspondingEvent = allEventsInRange.find(
								(e) => e.uuid === uuid,
							)
							if (correspondingEvent) usedEvents.push(correspondingEvent)
						})
						events = usedEvents
					}
					return (
						<CreateFormGridItem
							key={key}
							$fullWidth={fullWidth}
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
							}}
						>
							<label
								className="md-text--secondary"
								style={{
									fontSize: '12px',
									display: 'block',
									marginBottom: '0.5rem',
								}}
							>
								{isRequired ? `${usedTitle} *` : usedTitle}
							</label>
							<LoanEventCreateDialogSharegroupTable
								events={events}
								apartmentOptions={apartmentOptionsWithShareAmount}
								disabled={isDisabled}
								setValue={setValue}
								control={control}
								sort={sort}
								changeSort={changeSort}
							/>
							{errors[key]?.message && (
								<ErrorMessage>{errors[key]?.message}</ErrorMessage>
							)}
						</CreateFormGridItem>
					)
				}
			}

			return null
		})
	}

	return (
		<ViiluDialog
			id="loan-event-create-dialog"
			title={
				event
					? intl.formatMessage({
							defaultMessage: 'Tapahtuman tarkastelu',
							description: 'Title of preview event',
					  })
					: intl.formatMessage({
							defaultMessage: 'Luo uusi tapahtuma lainalle',
							description: 'Title of create new event for loan',
					  })
			}
			visible={true}
			actions={actions}
			onHide={onHide}
			focusOnMount={false}
			containFocus={false}
			dialogClassName="responsive-dialog"
			contentClassName="responsive-dialog-content"
			autosizeContent={false}
			modal
			portal
			paddedContent
		>
			<CreateFormGrid
				style={{
					gap: '0.5rem',
				}}
			>
				{renderInputs()}
			</CreateFormGrid>
		</ViiluDialog>
	)
}

export default LoanEventCreateDialog
